import { capitalizeFirstLetter } from '@/util/util';

import type { Improvement, ImprovementSeverity, ImprovementStatus, ReviewAction } from './types';

const SEVERITY_RANK: Record<ImprovementSeverity, number> = { high: 0, medium: 1, low: 2 };

/**
 * Review actions offered per current status (D3): pending items get the full
 * bar, approved/rejected can still be archived. `partially_addressed` and
 * `replaced` are system-managed and stay read-only; `resolved` is final.
 */
export const availableActions = (status: ImprovementStatus): ReviewAction[] => {
  if (status === 'pending') {
    return ['apply', 'dismiss', 'archive'];
  }
  if (status === 'approved' || status === 'rejected') {
    return ['archive'];
  }
  return [];
};

/**
 * Inbox ordering (D8): severity High → Medium → Low, then newest first.
 * The API only sorts by `-created`; severity grouping happens client-side.
 */
export const sortImprovements = (improvements: Improvement[]): Improvement[] =>
  [...improvements].sort((a, b) => {
    const bySeverity = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
    if (bySeverity !== 0) {
      return bySeverity;
    }
    return b.created.localeCompare(a.created);
  });

export const countPending = (improvements: Improvement[]): number =>
  improvements.filter((improvement) => improvement.status === 'pending').length;

/** Nav badge label; the list response has no total, so cap the display (D8). */
export const formatPendingCount = (count: number): string => (count > 99 ? '99+' : `${count}`);

export const filterByStatus = (
  improvements: Improvement[],
  status: ImprovementStatus | 'all'
): Improvement[] =>
  status === 'all'
    ? improvements
    : improvements.filter((improvement) => improvement.status === status);

/**
 * Which zero-state to show (D8): `waiting` when the brain has no improvements
 * at all (Omni hasn't produced any yet), `caught_up` when the current filter
 * just has nothing left to review, or null when there are rows to render.
 */
export const getEmptyState = (
  hasAny: boolean,
  visibleCount: number
): 'waiting' | 'caught_up' | null => {
  if (visibleCount > 0) {
    return null;
  }
  return hasAny ? 'caught_up' : 'waiting';
};

/** 'add_knowledge' → 'Add knowledge' (improvement_type / impact keys are free-form snake_case). */
export const humanizeKey = (key: string): string => {
  const words = key.replace(/[_-]+/g, ' ').trim();
  return words ? capitalizeFirstLetter(words) : key;
};

/** Impact values are an untyped record; render primitives plainly, nested blobs as JSON. */
export const formatImpactValue = (value: unknown): string => {
  if (value == null) {
    return '—';
  }
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
};
